/**
 * Иконки интерфейса — инлайновый SVG, без шрифтов и без библиотеки.
 *
 * Зачем свои. Раньше на их месте стояли эмодзи и текстовые стрелки («←», «▾»,
 * «×»): эмодзи на каждом телефоне рисуются по-своему и спорят цветом с
 * интерфейсом, а стрелка из шрифта пляшет по базовой линии. Контур в SVG
 * одинаков везде и красится через `currentColor`.
 *
 * Библиотека иконок — это сотни килобайт ради полутора десятков значков
 * (раздел 3: клиент лёгкий), поэтому они нарисованы здесь. Сетка 24×24, линия
 * 2 px со скруглёнными концами; значки нижней панели — залитые, как принято в
 * таб-баре iOS.
 *
 * Все иконки декоративны (`aria-hidden`): подпись действия несёт сама кнопка —
 * текстом или `aria-label`.
 */

import type { ReactNode } from 'react'

interface IconProps {
  size?: number
  className?: string
}

function Glyph({
  size = 24,
  className,
  children,
  filled = false,
}: IconProps & { children: ReactNode; filled?: boolean }) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={filled ? undefined : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

// --- нижняя панель ---------------------------------------------------------

/** «Сегодня»: гантель. */
export function IconDumbbell(props: IconProps) {
  return (
    <Glyph {...props} filled>
      <rect x="1.5" y="8.75" width="3" height="6.5" rx="1.4" />
      <rect x="5" y="5.75" width="3.6" height="12.5" rx="1.6" />
      <rect x="8" y="10.75" width="8" height="2.5" />
      <rect x="15.4" y="5.75" width="3.6" height="12.5" rx="1.6" />
      <rect x="19.5" y="8.75" width="3" height="6.5" rx="1.4" />
    </Glyph>
  )
}

/** «Программа»: календарь. */
export function IconCalendar(props: IconProps) {
  return (
    <Glyph {...props} filled>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.5 2.5a1 1 0 0 1 1 1v.75h7V3.5a1 1 0 1 1 2 0v.75h.5A3.5 3.5 0 0 1 21.5 7.75V17.5A3.5 3.5 0 0 1 18 21H6a3.5 3.5 0 0 1-3.5-3.5V7.75A3.5 3.5 0 0 1 6 4.25h.5V3.5a1 1 0 0 1 1-1ZM4.5 10v7.5A1.5 1.5 0 0 0 6 19h12a1.5 1.5 0 0 0 1.5-1.5V10h-15Z"
      />
      <rect x="6.75" y="12" width="3" height="2.6" rx="0.9" />
      <rect x="10.5" y="12" width="3" height="2.6" rx="0.9" />
      <rect x="14.25" y="12" width="3" height="2.6" rx="0.9" />
      <rect x="6.75" y="15.4" width="3" height="2.6" rx="0.9" />
      <rect x="10.5" y="15.4" width="3" height="2.6" rx="0.9" />
    </Glyph>
  )
}

/** «Прогресс»: растущие столбики. */
export function IconChart(props: IconProps) {
  return (
    <Glyph {...props} filled>
      <rect x="3.25" y="13" width="4.5" height="8" rx="1.5" />
      <rect x="9.75" y="8.5" width="4.5" height="12.5" rx="1.5" />
      <rect x="16.25" y="3" width="4.5" height="18" rx="1.5" />
    </Glyph>
  )
}

/** «Цели»: мишень. */
export function IconTarget(props: IconProps) {
  return (
    <Glyph {...props} filled>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16Zm0 2.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Zm0 2a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7Z"
      />
      <circle cx="12" cy="12" r="1.9" />
    </Glyph>
  )
}

/** «История»: часы. */
export function IconClock(props: IconProps) {
  return (
    <Glyph {...props} filled>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1 5a1 1 0 1 1 2 0v4.42l3 1.74a1 1 0 1 1-1 1.73l-3.5-2.02a1 1 0 0 1-.5-.87V7Z"
      />
    </Glyph>
  )
}

// --- действия ---------------------------------------------------------------

export function IconChevronLeft(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M15 5l-7 7 7 7" />
    </Glyph>
  )
}

export function IconChevronRight(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M9 5l7 7-7 7" />
    </Glyph>
  )
}

export function IconChevronDown(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M5 9l7 7 7-7" />
    </Glyph>
  )
}

export function IconClose(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Glyph>
  )
}

export function IconPlus(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M12 5v14M5 12h14" />
    </Glyph>
  )
}

export function IconMinus(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M5 12h14" />
    </Glyph>
  )
}

export function IconCheck(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </Glyph>
  )
}

/** Запуск тренировки. */
export function IconPlay(props: IconProps) {
  return (
    <Glyph {...props} filled>
      <path d="M7 4.6c0-1.2 1.3-1.9 2.3-1.3l11 7.4c.9.6.9 2 0 2.6l-11 7.4c-1 .7-2.3 0-2.3-1.3V4.6Z" />
    </Glyph>
  )
}

/** Сообщение компаньона. */
export function IconSparkle(props: IconProps) {
  return (
    <Glyph {...props} filled>
      <path d="M10 2.5c.4 0 .8.3.9.7l1 3.6a5 5 0 0 0 3.4 3.4l3.6 1a1 1 0 0 1 0 1.9l-3.6 1a5 5 0 0 0-3.4 3.4l-1 3.6a1 1 0 0 1-1.9 0l-1-3.6a5 5 0 0 0-3.4-3.4l-3.6-1a1 1 0 0 1 0-1.9l3.6-1A5 5 0 0 0 8 6.8l1-3.6c.2-.4.5-.7 1-.7Z" />
      <path d="M19 1.5c.3 0 .5.2.6.4l.4 1.4c.2.6.6 1 1.2 1.2l1.4.4a.6.6 0 0 1 0 1.2l-1.4.4c-.6.2-1 .6-1.2 1.2l-.4 1.4a.6.6 0 0 1-1.2 0l-.4-1.4a1.8 1.8 0 0 0-1.2-1.2l-1.4-.4a.6.6 0 0 1 0-1.2l1.4-.4c.6-.2 1-.6 1.2-1.2l.4-1.4c.1-.2.3-.4.6-.4Z" />
    </Glyph>
  )
}
